import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { Alert, AlertTitle, Button, Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { ApiError, apiRequest, authStore, type QueryParams } from "./client";

export function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "An unexpected error occurred.";
}

export function ApiErrorPanel({
  error,
  onRetry,
  onRestart,
}: {
  error: unknown;
  onRetry?: () => void;
  onRestart?: () => void;
}) {
  if (!error) return null;
  const apiError = error instanceof ApiError ? error : undefined;
  const title =
    apiError?.status === 404
      ? "Not found"
      : apiError?.status === 403
        ? "No access"
        : "Request failed";
  return (
    <Alert severity="error" sx={{ my: 2 }}>
      <AlertTitle>{title}</AlertTitle>
      {errorMessage(error)}
      {apiError?.violations.map(({ field, message }) => (
        <Typography variant="body2" key={field}>
          {field}: {message}
        </Typography>
      ))}
      {apiError?.requestId && (
        <Typography
          variant="caption"
          display="block"
          sx={{ userSelect: "all", overflowWrap: "anywhere", mt: 1 }}
        >
          Request ID: {apiError.requestId}
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {apiError?.error === "invalid_cursor" && onRestart ? (
          <Button onClick={onRestart} color="inherit">
            Restart from first page
          </Button>
        ) : (
          onRetry && (
            <Button onClick={onRetry} color="inherit">
              Retry
            </Button>
          )
        )}
      </Stack>
    </Alert>
  );
}

export function useApi<T>(
  path: string | null,
  params: QueryParams = {},
  revision = 0,
) {
  const auth = useSyncExternalStore(
    authStore.subscribe,
    authStore.getSnapshot,
    authStore.getSnapshot,
  );
  const identity = auth.authenticated ? auth.user?.userId : null;
  const serialized = JSON.stringify(params);
  const key = JSON.stringify([path, serialized, identity, revision]);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState<{
    key: string;
    data?: T;
    error?: unknown;
    loading: boolean;
  }>({ key: "", loading: true });
  useEffect(() => {
    const controller = new AbortController();
    if (!path || !identity) {
      setState({ key, loading: false });
      return () => controller.abort();
    }
    setState({ key, loading: true });
    void apiRequest<T>(path, {
      params: JSON.parse(serialized),
      signal: controller.signal,
    }).then(
      (data) => {
        if (!controller.signal.aborted) setState({ key, data, loading: false });
      },
      (error) => {
        if (!controller.signal.aborted)
          setState({ key, error, loading: false });
      },
    );
    return () => controller.abort();
  }, [path, serialized, identity, revision, key, retry]);
  const current =
    state.key === key
      ? state
      : {
          loading: Boolean(path && identity),
          data: undefined,
          error: undefined,
        };
  return {
    data: current.data,
    error: current.error,
    loading: current.loading,
    reload: () => setRetry((value) => value + 1),
  };
}

interface BrandScope {
  brandId: number | undefined;
  setBrandId: (value: number | undefined) => void;
}
const BrandContext = createContext<BrandScope | null>(null);
function parseBrand(value: string | null): number | undefined {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}
export function BrandScopeProvider({ children }: { children: ReactNode }) {
  const auth = useSyncExternalStore(
    authStore.subscribe,
    authStore.getSnapshot,
    authStore.getSnapshot,
  );
  const [params, setParams] = useSearchParams();
  const rawBrand = params.get("brandId");
  const urlBrand = parseBrand(rawBrand);
  const [selected, setSelected] = useState<number | undefined>(urlBrand);
  const platform = auth.user?.role === "PLATFORM_ADMIN";
  const brandId = platform
    ? rawBrand !== null
      ? urlBrand
      : selected
    : (auth.user?.brandId ?? undefined);
  useEffect(() => {
    if (platform && rawBrand !== null) setSelected(urlBrand);
  }, [platform, rawBrand, urlBrand]);
  const setBrandId = (value: number | undefined) => {
    if (!platform) return;
    setSelected(value);
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === undefined) next.delete("brandId");
        else next.set("brandId", String(value));
        next.delete("cursor");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };
  return (
    <BrandContext.Provider value={{ brandId, setBrandId }}>
      {children}
    </BrandContext.Provider>
  );
}
export function useBrandScope(): BrandScope {
  const scope = useContext(BrandContext);
  if (!scope) throw new Error("BrandScopeProvider is missing.");
  return scope;
}
